import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, AlertTriangle } from 'lucide-react';
import { createDictationEngine, DictationEngine } from '../services/dictation';

interface Props {
  onInterim?: (text: string) => void;
  onFinal: (text: string) => void;
  disabled?: boolean;
  size?: number;
  language?: string;
  /** Per-session terms to bias recognition toward (e.g. the client's name). */
  keyterms?: string[];
}

/**
 * VoiceNoteButton
 *
 * Hands-free voice-to-text for in-session quick notes. Text-only (no raw-audio
 * archive). Backed by the shared dictation engine — Web Speech by default, or
 * Deepgram nova-2 streaming when REACT_APP_DICTATION_PROVIDER=deepgram (the path
 * that works in iOS WKWebView). Calls onInterim with the running transcript and
 * onFinal with the complete text when the user stops.
 */
export const VoiceNoteButton: React.FC<Props> = ({
  onInterim,
  onFinal,
  disabled,
  size = 18,
  language = 'en-US',
  keyterms,
}) => {
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const engineRef = useRef<DictationEngine | null>(null);
  const finalTextRef = useRef<string>('');
  const interimRef = useRef<string>('');
  // Guards against onFinal firing twice for a single dictation.
  const firedRef = useRef(false);

  // Build the engine once. Its callbacks assemble the running transcript.
  if (engineRef.current === null) {
    engineRef.current = createDictationEngine({
      onFinal: (text) => {
        finalTextRef.current = (finalTextRef.current + ' ' + text).trim();
        interimRef.current = '';
        onInterim?.(finalTextRef.current);
      },
      onInterim: (text) => {
        interimRef.current = text;
        onInterim?.((finalTextRef.current + ' ' + text).trim());
      },
      onError: (msg) => setError(msg),
    });
  }
  const supported = engineRef.current?.isSupported() ?? false;

  useEffect(() => {
    return () => {
      void engineRef.current?.stop();
    };
  }, []);

  const stop = async () => {
    try {
      await engineRef.current?.stop();
    } catch {
      /* noop */
    }
    setRecording(false);
    const finalText = finalTextRef.current.trim();
    if (finalText && !firedRef.current) {
      firedRef.current = true;
      onFinal(finalText);
    }
  };

  const start = async () => {
    if (!supported) {
      setError(
        'Voice input is not supported in this browser. Try Chrome or Edge on desktop or Android.'
      );
      return;
    }
    setError(null);
    finalTextRef.current = '';
    interimRef.current = '';
    firedRef.current = false;
    setRecording(true);
    try {
      await engineRef.current?.start({ language, keyterms });
    } catch (err: any) {
      setError(err?.message || 'Failed to start voice input');
      setRecording(false);
    }
  };

  if (!supported) {
    // Render a disabled, tooltip-only button rather than nothing — keeps
    // layout stable across browsers.
    return (
      <button
        type="button"
        disabled
        title="Voice input not supported in this browser"
        style={{
          padding: '10px 12px',
          borderRadius: '6px',
          border: '1px solid #d1d5db',
          backgroundColor: '#f3f4f6',
          color: '#9ca3af',
          cursor: 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <MicOff size={size} />
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => (recording ? stop() : start())}
        disabled={disabled}
        title={recording ? 'Stop recording' : 'Tap to dictate a quick note'}
        aria-pressed={recording}
        style={{
          padding: '10px 14px',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: recording ? '#dc2626' : '#4F6367',
          color: 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          opacity: disabled ? 0.5 : 1,
          fontSize: '14px',
          fontWeight: 600,
          animation: recording ? 'vera-mic-pulse 1.2s ease-in-out infinite' : undefined,
        }}
      >
        {recording ? <MicOff size={size} /> : <Mic size={size} />}
        {recording ? 'Stop' : 'Voice'}
      </button>
      {error && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#dc2626',
            fontSize: '12px',
            marginTop: '6px',
          }}
        >
          <AlertTriangle size={14} />
          {error}
        </div>
      )}
      <style>{`
        @keyframes vera-mic-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
        }
      `}</style>
    </>
  );
};

export default VoiceNoteButton;
